import clsx from 'clsx';
import type { NdtIndicationRow } from './drillDownTypes';
import { isNdtLogRejected } from '../../../utils/fetchNdtIndicationLogsApi';

interface NdtIndicationDetailPanelProps {
  log: NdtIndicationRow | null;
}

function text(value: string | number | null | undefined): string {
  const shown = value == null ? '' : String(value).trim();
  return shown || '—';
}

function defectDimensions(log: NdtIndicationRow): string {
  const parts = [log.defectLength, log.defectDepth, log.defectHeight]
    .map((value) => (value != null && String(value).trim() ? String(value) : null))
    .filter(Boolean);
  return parts.length ? parts.join(' / ') : '—';
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[rgba(0,39,63,0.03)] px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-medium text-blueprint-blue">{value}</p>
    </div>
  );
}

export const NdtIndicationDetailPanel = ({ log }: NdtIndicationDetailPanelProps) => {
  if (!log) {
    return (
      <div className="flex h-full min-h-[200px] flex-1 items-center justify-center rounded-xl border border-dashed border-[#E2E5EA] bg-white/60 p-6">
        <p className="text-center text-sm text-heritage-grey">
          Select an indication on the left to see the full NDT record.
        </p>
      </div>
    );
  }

  const rejected = isNdtLogRejected(log);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
      <div className="shrink-0 border-b border-[#E2E5EA] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-[15px] font-semibold text-blueprint-blue">
            {text(log.defectType) === '—' ? 'NDT indication' : text(log.defectType)}
          </h3>
          <span
            className={clsx(
              'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
              rejected
                ? 'bg-[rgba(196,0,31,0.08)] text-readable-red'
                : 'bg-[rgba(0,39,63,0.06)] text-heritage-grey',
            )}
          >
            {text(log.evaluation)}
          </span>
        </div>
        <p className="mt-1 text-xs text-heritage-grey">
          {text(log.date)}
          {log.passName ? ` · ${text(log.passName)}` : ''}
        </p>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Date" value={text(log.date)} />
          <Field label="Shift" value={text(log.shift)} />
          <Field label="Pass" value={text(log.passName)} />
          <Field label="Location" value={text(log.location)} />
          <Field label="L / D / H" value={defectDimensions(log)} />
          <Field label="Welders" value={text(log.weldersN)} />
          <Field label="Repair" value={text(log.repair)} />
          <Field label="WPS" value={text(log.wpsNumber)} />
          <Field label="Critical weld" value={text(log.criticalWeld)} />
          <Field label="Workmanship" value={text(log.acceptedWorkmanship)} />
          <Field label="Root cause" value={text(log.rootCause)} />
        </div>
        <Field label="Comments" value={text(log.comments)} />
      </div>
    </div>
  );
};
